// Shared pieces for the canvas piano rolls, ported from the ISMIR'26 demo site.
//
// Both rolls draw onto a <canvas> and overlay a plain <div> playhead that is
// driven by the <audio> element's clock rather than by any MIDI player, which
// is what keeps the two in sync no matter how the audio was rendered.

import { useCallback, useEffect, useRef, useState } from 'react';

// sns.color_palette('tab20', desat=0.6), with the first pair swapped for a
// magenta/violet that reads better against the site's blue accents.
export const SEABORN_TAB20_COLORS = [
  '#db359e', '#662075', '#cf823e', '#e4bb93',
  '#438943', '#a3ce9b', '#b34a4b', '#eaacab',
  '#9378ac', '#c4b7ce', '#7f5f58', '#baa29e',
  '#cd8dba', '#eac3d4', '#7f7f7f', '#c7c7c7',
  '#9d9e41', '#cbcb9d', '#3ca0aa', '#acd0d7',
];

// 16 General MIDI instrument families; program p belongs to family floor(p / 8).
// Drums are tracked separately since they are a channel, not a program.
export const GM_FAMILIES = [
  { full: 'Piano',                short: 'Piano' },
  { full: 'Chromatic Percussion', short: 'Chrom. Perc.' },
  { full: 'Organ',                short: 'Organ' },
  { full: 'Guitar',               short: 'Guitar' },
  { full: 'Bass',                 short: 'Bass' },
  { full: 'Strings',              short: 'Strings' },
  { full: 'Ensemble',             short: 'Ensemble' },
  { full: 'Brass',                short: 'Brass' },
  { full: 'Reed',                 short: 'Reed' },
  { full: 'Pipe',                 short: 'Pipe' },
  { full: 'Synth Lead',           short: 'Synth Lead' },
  { full: 'Synth Pad',            short: 'Synth Pad' },
  { full: 'Synth Effects',        short: 'Synth FX' },
  { full: 'Ethnic',               short: 'Ethnic' },
  { full: 'Percussive',           short: 'Percussive' },
  { full: 'Sound Effects',        short: 'Sound FX' },
];

// Alternates between the darker and softer halves of tab20 so that families
// adjacent in the legend stay distinguishable.
const FAMILY_COLOR_INDICES = [0, 2, 4, 6, 8, 10, 12, 16, 18, 1, 3, 5, 7, 9, 11, 13];

export const DRUM_KEY = 'drums';
export const DRUM_COLOR = '#1f1f1f';
export const DRUM_LABEL = 'Drums';

export const familyColor = (familyIdx) =>
  SEABORN_TAB20_COLORS[FAMILY_COLOR_INDICES[familyIdx % 16]];

export const programToFamily = (program) =>
  Math.max(0, Math.min(15, Math.floor((program ?? 0) / 8)));

export const groupKeyForTrack = (track) =>
  track.is_drum ? DRUM_KEY : `fam-${programToFamily(track.program)}`;

export const colorForKey = (key) => {
  if (key === DRUM_KEY) return DRUM_COLOR;
  return familyColor(parseInt(key.slice(4), 10));
};

export const labelForKey = (key) => {
  if (key === DRUM_KEY) return DRUM_LABEL;
  const famIdx = parseInt(key.slice(4), 10);
  return GM_FAMILIES[famIdx]?.short ?? `Family ${famIdx}`;
};

export const fullLabelForKey = (key) => {
  if (key === DRUM_KEY) return DRUM_LABEL;
  const famIdx = parseInt(key.slice(4), 10);
  return GM_FAMILIES[famIdx]?.full ?? `Family ${famIdx}`;
};

const NOTE_NAMES = ['C', 'C#', 'D', 'D#', 'E', 'F', 'F#', 'G', 'G#', 'A', 'A#', 'B'];

export const pitchToName = (pitch) =>
  `${NOTE_NAMES[((pitch % 12) + 12) % 12]}${Math.floor(pitch / 12) - 1}`;

// Downbeats are encoded as pitch 34 in the click track, plain beats as 32.
export const parseMeasures = (clickNotes) => {
  if (!clickNotes || clickNotes.length === 0) return [];
  return clickNotes
    .filter((n) => n.pitch === 34)
    .map((n) => n.time)
    .sort((a, b) => a - b);
};

export const PALETTE = {
  background: '#fbfaf9',
  measureEven: '#f4f2f0',
  measureOdd: '#fbfaf9',
  measureLine: '#e2e2df',
  beatLine: '#eeedeb',
  pitchLine: '#e6e5e2',
  pitchLabel: '#a9a6a1',
  measureLabel: '#a9a6a1',
  emptyFill: '#f6f5f3',
  emptyText: '#a0a0a0',
};

export const roundedRect = (ctx, x, y, w, h, r) => {
  const radius = Math.min(r, h / 2, w / 2);
  ctx.beginPath();
  ctx.moveTo(x + radius, y);
  ctx.lineTo(x + w - radius, y);
  ctx.arcTo(x + w, y, x + w, y + radius, radius);
  ctx.lineTo(x + w, y + h - radius);
  ctx.arcTo(x + w, y + h, x + w - radius, y + h, radius);
  ctx.lineTo(x + radius, y + h);
  ctx.arcTo(x, y + h, x, y + h - radius, radius);
  ctx.lineTo(x, y + radius);
  ctx.arcTo(x, y, x + radius, y, radius);
  ctx.fill();
};

export const drawEmptyState = (canvas, ctx, dpr, message) => {
  const width = canvas.offsetWidth;
  canvas.width = width * dpr;
  canvas.height = 80 * dpr;
  canvas.style.height = '80px';
  ctx.scale(dpr, dpr);
  ctx.fillStyle = PALETTE.emptyFill;
  ctx.fillRect(0, 0, width, 80);
  ctx.fillStyle = PALETTE.emptyText;
  ctx.font = '13px Inter, -apple-system, sans-serif';
  ctx.textAlign = 'center';
  ctx.fillText(message, width / 2, 44);
};

// Re-draws need the element's CSS width, and canvases do not reflow on their
// own, so watch the wrapper and hand the width back as render state.
export const useElementWidth = (ref) => {
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    setWidth(el.offsetWidth);
    if (typeof ResizeObserver === 'undefined') return undefined;

    const observer = new ResizeObserver((entries) => {
      const next = entries[0]?.contentRect?.width;
      if (next) setWidth(Math.round(next));
    });
    observer.observe(el);
    return () => observer.disconnect();
  }, [ref]);

  return width;
};

// Drives the playhead div from audio.currentTime against the geometry the last
// draw stashed in layoutRef. The audio element is read inside the loop so the
// hook keeps working when the <audio> mounts after the canvas.
export const usePlayhead = (audioRef, layoutRef, playheadRef) => {
  useEffect(() => {
    let raf = null;

    const animate = () => {
      const audio = audioRef?.current;
      const playhead = playheadRef.current;
      const layout = layoutRef.current;

      if (playhead) {
        if (audio && layout && layout.maxTime > 0 && (!audio.paused || audio.currentTime > 0)) {
          const progress = Math.min(audio.currentTime / layout.maxTime, 1);
          playhead.style.left = `${layout.leftPad + progress * layout.drawWidth}px`;
          playhead.style.top = `${layout.topPad}px`;
          playhead.style.height = `${layout.height - layout.topPad}px`;
          playhead.style.opacity = '1';
        } else {
          playhead.style.opacity = '0';
        }
      }
      raf = requestAnimationFrame(animate);
    };

    raf = requestAnimationFrame(animate);
    return () => {
      if (raf !== null) cancelAnimationFrame(raf);
    };
  }, [audioRef, layoutRef, playheadRef]);
};

// A tab can show 20 players at once; without this they all talk over each other.
export const pauseOtherAudio = (event) => {
  const current = event.target;
  document.querySelectorAll('audio').forEach((el) => {
    if (el !== current && !el.paused) el.pause();
  });
};

// Remote MP3s are answered by huggingface.co with a cross-origin 302 to a
// signed CDN URL. Safari's media loader refuses that hop and fails the request
// with a 403, while the note JSON is fine because its redirect stays on
// huggingface.co. So resolve the final URL ourselves with a HEAD (no custom
// headers, so no preflight) and hand the element a URL it can fetch directly.
// Signed URLs last an hour; we cache well inside that and re-resolve if
// playback errors, in case a tab sat open past expiry.
const RESOLVED_TTL_MS = 45 * 60 * 1000;
const MAX_RESOLVE_RETRIES = 2;
const resolvedAudioUrls = new Map();

const freshlyResolved = (url) => {
  const hit = resolvedAudioUrls.get(url);
  return hit && Date.now() - hit.at < RESOLVED_TTL_MS ? hit.href : null;
};

export const useResolvedAudioSrc = (url) => {
  const isRemote = /^https?:\/\//i.test(url || '');
  const [src, setSrc] = useState(() => (isRemote ? freshlyResolved(url) : url || null));
  const [attempt, setAttempt] = useState(0);
  const retries = useRef(0);

  useEffect(() => {
    retries.current = 0;
  }, [url]);

  useEffect(() => {
    if (!url) {
      setSrc(null);
      return undefined;
    }
    if (!isRemote) {
      setSrc(url);
      return undefined;
    }

    const cached = freshlyResolved(url);
    if (cached) {
      setSrc(cached);
      return undefined;
    }

    let cancelled = false;
    fetch(url, { method: 'HEAD' })
      .then((res) => {
        const href = res.url || url;
        resolvedAudioUrls.set(url, { href, at: Date.now() });
        if (!cancelled) setSrc(href);
      })
      // Fall back to the original URL and let the browser attempt the redirect
      // itself, which still works everywhere except Safari.
      .catch(() => {
        if (!cancelled) setSrc(url);
      });

    return () => {
      cancelled = true;
    };
  }, [url, isRemote, attempt]);

  const refresh = useCallback(() => {
    if (!isRemote || retries.current >= MAX_RESOLVE_RETRIES) return;
    retries.current += 1;
    resolvedAudioUrls.delete(url);
    setAttempt((n) => n + 1);
  }, [url, isRemote]);

  return [src, refresh];
};

export const useJsonResource = (url) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (!url) return undefined;

    let cancelled = false;
    setData(null);
    setError(null);

    fetch(url)
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        return r.json();
      })
      .then((json) => {
        if (!cancelled) setData(json);
      })
      .catch((e) => {
        if (!cancelled) setError(e.message);
      });

    return () => {
      cancelled = true;
    };
  }, [url]);

  return { data, error };
};
